// ============================================================
// Breadcrumb.js
// Hierarchical navigation trail for drill-down detail pages
// (Client detail, User detail, future Phase 2 detail surfaces).
//
// items: Array<{ label: string, to?: string }>
//   - Parent segments carry `to` and render as react-router <Link>s.
//   - The LAST item is conventionally the current page — it MUST
//     render as plain text (aria-current="page"), not a link, even
//     if `to` is provided. The last-segment rule is what makes the
//     component drop-in for any detail page without callers having
//     to remember to omit `to` on the leaf.
//
// Separators: chevron-right between segments, hidden from a11y
// (ChevronRight is decorative — the <ol>/<li> structure conveys
// the hierarchy to assistive tech).
//
// Empty `items` (zero-length) renders nothing — defensive against
// callers that pass [] during a loading state.
// ============================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import './Breadcrumb.css';

export default function Breadcrumb({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <ol className="breadcrumb__list">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={i} className="breadcrumb__item">
              {item.to && !isLast ? (
                <Link to={item.to} className="breadcrumb__link">{item.label}</Link>
              ) : (
                <span
                  className="breadcrumb__current"
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              )}
              {!isLast && (
                <ChevronRight
                  size={14}
                  strokeWidth={1.75}
                  className="breadcrumb__sep"
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
